/**
 * Arabic labels and clinical term glossary for report print / PDF output.
 * English keys are preserved in stored reports; translation runs at export time.
 */

/** Section keys omitted from print/PDF (demographics & physician stay in the form only). */
export const REPORT_SECTIONS_PRINT_EXCLUDE = new Set([
    'Patient Demographics',
    'Referring / Attending Physician',
    'Referring Physician',
]);

/** Preferred order for clinical sections on a single A5 print page. */
export const REPORT_CLINICAL_PRINT_ORDER = [
    'Reason for Visit',
    'Reason for Referral',
    'Examination Summary',
    'Diagnosis',
    'Plan / Recommendations',
];

export const isReportSectionPrintExcluded = (title) =>
    REPORT_SECTIONS_PRINT_EXCLUDE.has(String(title || '').trim());

/** Keys for non-template blocks (freeform, notes, signature) in sectionInclusion maps. */
export const REPORT_SECTION_INCLUSION_KEYS = {
    FREEFORM: '__freeform__',
    DOCTOR_NOTES: '__doctorNotes__',
    SIGNATURE: '__signature__',
    CLINICAL_APPENDIX: '__clinicalAppendix__',
};

/** Build a sectionInclusion map with every listed section defaulting to included. */
export const defaultReportSectionInclusion = (sectionKeys = []) => {
    const inclusion = {};
    sectionKeys.forEach((key) => {
        inclusion[key] = true;
    });
    inclusion[REPORT_SECTION_INCLUSION_KEYS.FREEFORM] = true;
    inclusion[REPORT_SECTION_INCLUSION_KEYS.DOCTOR_NOTES] = true;
    inclusion[REPORT_SECTION_INCLUSION_KEYS.SIGNATURE] = true;
    inclusion[REPORT_SECTION_INCLUSION_KEYS.CLINICAL_APPENDIX] = true;
    return inclusion;
};

/**
 * Whether a section/block should appear in print/PDF.
 * When sectionInclusion is set, user toggles win; otherwise legacy print exclusions apply.
 */
export const isReportSectionIncluded = (title, sectionInclusion) => {
    if (sectionInclusion != null && typeof sectionInclusion === 'object') {
        if (Object.prototype.hasOwnProperty.call(sectionInclusion, title)) {
            return sectionInclusion[title] !== false;
        }
        return true;
    }
    return !isReportSectionPrintExcluded(title);
};

/** Report section titles (English key → Arabic display) */
export const REPORT_SECTION_TITLES_AR = {
    'Patient Demographics': 'بيانات المريض',
    'Referring / Attending Physician': 'الطبيب المحول / المعالج',
    'Referring Physician': 'الطبيب المحول',
    'Reason for Visit': 'سبب الزيارة',
    'Reason for Referral': 'سبب التحويل',
    'Examination Summary': 'ملخص الفحص',
    Diagnosis: 'التشخيص',
    'Plan / Recommendations': 'الخطة / التوصيات',
    'Pre-operative Diagnosis': 'التشخيص قبل العملية',
    'Procedure Performed': 'الإجراء المنفذ',
    'Operative Findings': 'النتائج أثناء العملية',
    Complications: 'المضاعفات',
    'Post-operative Instructions': 'تعليمات ما بعد العملية',
    'Visual Acuity': 'حدة الإبصار',
    'Color Vision': 'رؤية الألوان',
    'Field of Vision': 'المجال البصري',
    Conclusion: 'الخلاصة',
    Recommendations: 'التوصيات',
    'Visual Status': 'الحالة البصرية',
    'Functional Limitations': 'القيود الوظيفية',
    'Best Corrected Vision': 'أفضل تصحيح بصري',
    Prognosis: 'المآل',
    'Patient Information': 'معلومات المريض',
    'Medical History': 'التاريخ المرضي',
    'Current Condition': 'الحالة الحالية',
    'Treatment Required': 'العلاج المطلوب',
    'Cost Estimate': 'تقدير التكلفة',
    'Additional Notes': 'ملاحظات إضافية',
    'Additional clinical summary': 'ملخص سريري إضافي',
};

/** Common report document titles */
export const REPORT_DOCUMENT_TITLES_AR = {
    'General Ophthalmology Report': 'تقرير رمد عام',
    'Surgical Report': 'تقرير جراحي',
    'Referral Letter': 'خطاب تحويل',
    'Fitness Certificate': 'شهادة لياقة طبية',
    'Disability Assessment': 'تقييم عجز',
    'Insurance Report': 'تقرير تأمين',
    'Medical Report': 'تقرير طبي',
    'Medical History Summary': 'ملخص التاريخ الطبي',
    'EYE EXAMINATION REPORT': 'تقرير فحص العيون',
    'Bed Rest Certificate': 'شهادة راحة في الفراش',
    'Bed rest': 'راحة في الفراش',
    'Post-Cataract Surgery Report': 'تقرير ما بعد عملية المياه البيضاء',
    'Post-Glaucoma Surgery Report': 'تقرير ما بعد عملية الجلوكوما',
    'Diabetic Eye Screening Report': 'تقرير فحص عيون السكري',
    'Glaucoma Evaluation Report': 'تقرير تقييم الجلوكوما',
    'Fitness for Surgery Certificate': 'شهادة لياقة للعملية',
    'Vision Certificate (Driving/Work)': 'شهادة رؤية (قيادة/عمل)',
    'Retina Specialist Referral': 'تحويل لأخصائي الشبكية',
    'Oculoplastics Referral': 'تحويل لتجميل العيون',
    'Keratoconus / Corneal Topography Report': 'تقرير القرنية والطبوغرافيا',
    'Dry Eye / Ocular Surface Report': 'تقرير جفاف العين وسطح العين',
    'Corneal Specialist Referral': 'تحويل لأخصائي القرنية',
    'Insurance Prior Authorization Medical Report': 'تقرير موافقة تأمين مسبقة',
    'Post-Intravitreal Injection Report': 'تقرير ما بعد حقن الجسم الزجاجي',
};

/**
 * Clinical phrases in exam summaries and prefilled text (longer phrases first).
 */
const CLINICAL_PHRASE_REPLACEMENTS = [
    ['Examination date:', 'تاريخ الفحص:'],
    ['Unaided VA — OD:', 'حدة الإبصار بدون تصحيح — يمين:'],
    ['Unaided VA — OS:', 'حدة الإبصار بدون تصحيح — يسار:'],
    ['Unaided VA — OD', 'حدة الإبصار بدون تصحيح — يمين'],
    ['Unaided VA — OS', 'حدة الإبصار بدون تصحيح — يسار'],
    ['IOP — OD:', 'ضغط العين — يمين:'],
    ['IOP — OS:', 'ضغط العين — يسار:'],
    ['Subjective refraction — OD:', 'التصحيح الذاتي — يمين:'],
    ['Subjective refraction — OS:', 'التصحيح الذاتي — يسار:'],
    ['Autorefraction — OD:', 'القياس الآلي — يمين:'],
    ['Autorefraction — OS:', 'القياس الآلي — يسار:'],
    ['Exam notes:', 'ملاحظات الفحص:'],
    ['Latest glasses prescription:', 'آخر وصفة نظارة:'],
    ['Current medications:', 'الأدوية الحالية:'],
    ['Treatment plan:', 'خطة العلاج:'],
    ['Latest investigation:', 'آخر فحص:'],
    ['Name:', 'الاسم:'],
    ['Patient ID:', 'كود المريض:'],
    ['Date of birth:', 'تاريخ الميلاد:'],
    ['Age:', 'العمر:'],
    ['Chief complaint:', 'الشكوى الرئيسية:'],
    ['Medical history:', 'التاريخ المرضي:'],
    ['Anterior segment', 'المقدمة'],
    ['Posterior segment', 'القسم الخلفي'],
    ['Ocular motility', 'حركة العين'],
    ['Neuro-ophthalmic', 'عصبي بصري'],
    ['Right Eye (OD)', 'العين اليمنى (يمين)'],
    ['Left Eye (OS)', 'العين اليسرى (يسار)'],
    ['Right Eye (OD):', 'العين اليمنى (يمين):'],
    ['Left Eye (OS):', 'العين اليسرى (يسار):'],
    ['Visual Acuity (Unaided):', 'حدة الإبصار (بدون تصحيح):'],
    ['Subjective Refraction:', 'التصحيح الذاتي:'],
    ['Intraocular Pressure:', 'ضغط العين:'],
    ['Lids & Lashes', 'الجفون والرموش'],
    ['Adnexa & Orbit', 'الملحقات والمدار'],
    ['Anterior Segment:', 'المقدمة:'],
    ['Posterior Segment (Fundus):', 'القسم الخلفي (قاع العين):'],
    ['Neuro-ophthalmic:', 'عصبي بصري:'],
    ['Diagnosis:', 'التشخيص:'],
    ['Additional Notes:', 'ملاحظات إضافية:'],
    [' mmHg', ' مم زئبق'],
    ['mmHg', 'مم زئبق'],
    [' OD:', ' يمين:'],
    [' OS:', ' يسار:'],
    [' OD ', ' يمين '],
    [' OS ', ' يسار '],
    [' OD,', ' يمين،'],
    [' OS,', ' يسار،'],
    [' OD/', ' يمين/'],
    [' OS/', ' يسار/'],
    [' OD', ' يمين'],
    [' OS', ' يسار'],
    [' OU', ' كلتا العينين'],
    [' VA ', ' ح.إ '],
    [' VA:', ' حدة الإبصار:'],
    ['IOP', 'ضغط العين'],
    ['Diagnosis', 'التشخيص'],
    ['Examination Summary', 'ملخص الفحص'],
    ['Patient Demographics', 'بيانات المريض'],
    ['Plan / Recommendations', 'الخطة / التوصيات'],
    ['Reason for Visit', 'سبب الزيارة'],
    ['Reason for Referral', 'سبب التحويل'],
    ['Referring Physician', 'الطبيب المحول'],
    ['Referring / Attending Physician', 'الطبيب المحول / المعالج'],
    ['Lids:', 'الجفون:'],
    ['Adnexa:', 'الملحقات:'],
    ['Pupils:', 'البؤبؤ:'],
    ['Lids', 'الجفون'],
    ['Adnexa', 'الملحقات'],
    ['Pupils', 'البؤبؤ'],
];

export const translateReportSectionTitle = (title, lang = 'en') => {
    if (!title || lang !== 'ar') return title;
    return REPORT_SECTION_TITLES_AR[title] || title;
};

export const translateReportDocumentTitle = (title, lang = 'en') => {
    if (!title || lang !== 'ar') return title;
    const trimmed = String(title).trim();
    return REPORT_DOCUMENT_TITLES_AR[trimmed] || REPORT_DOCUMENT_TITLES_AR[trimmed.toUpperCase()] || trimmed;
};

/** Replace known English clinical labels in report body text for Arabic print. */
export const translateReportBodyForPrint = (text, lang = 'en') => {
    if (!text || lang !== 'ar') return text;
    let out = String(text);
    for (const [en, ar] of CLINICAL_PHRASE_REPLACEMENTS) {
        out = out.split(en).join(ar);
    }
    return out;
};

export const localizeReportBlock = (block, lang = 'en') => {
    if (!block) return block;
    return {
        ...block,
        title: block.title ? translateReportSectionTitle(block.title, lang) : block.title,
        body: translateReportBodyForPrint(block.body, lang),
    };
};

export const localizeReportBlocks = (blocks, lang = 'en') => {
    if (!Array.isArray(blocks) || lang !== 'ar') return blocks || [];
    return blocks.map((b) => localizeReportBlock(b, lang));
};
